import { DimensionDefinition } from 'libs/end-to-end/src/types/databases/dimensions/dimension-definition';
import { getTotalTestCount } from 'libs/end-to-end/src/tests-utils';
import { Test } from 'libs/end-to-end/src/tests-utils';
import { getCalibrationScore } from 'libs/end-to-end/src/tests-utils';
import { getTestCountsByBiasTypeAndDimension } from 'libs/end-to-end/src/tests-utils';
import React, { useMemo } from 'react';
import { DefaultPolarDataPoint } from 'apps/sigma2-ui/src/components/PolarChart';
import { PolarChart } from 'apps/sigma2-ui/src/components/PolarChart';
import { DimensionRadarSliceLayer } from 'apps/sigma2-ui/src/components/DimensionRadarSliceLayer';
import { DimensionTooltip } from 'apps/sigma2-ui/src/components/DimensionRadarSliceLayer';
import { CalibrationFraming } from 'libs/end-to-end/src/calibration-framing';

export type DimensionPolarChartData = {
  tests: Test[];
  dimensions: DimensionDefinition[];
};

export type DimensionPolarChartProps = {
  data: DimensionPolarChartData;
  focusArea: string;
  onDotClick?: () => void;
  calibrationFraming: CalibrationFraming;
};

const EmptyTestResults = {
  passing: 0,
  incomplete: 0,
  failing: 0,
};

export const DimensionPolarChart: React.FC<DimensionPolarChartProps> = ({
  data,
  focusArea,
  onDotClick,
  calibrationFraming,
}: DimensionPolarChartProps) => {
  const dataPoints: DefaultPolarDataPoint[] = useMemo(() => {
    const { tests, dimensions } = data;
    const dataPoints: DefaultPolarDataPoint[] = [];

    const allTestResults = getTestCountsByBiasTypeAndDimension(tests);
    for (const dimension of dimensions) {
      const informationResults =
        allTestResults['Information'].dimensions[
          dimension.columnHeaderValue.toLowerCase()
        ] || EmptyTestResults;
      const structuralResults =
        allTestResults['Structural'].dimensions[
          dimension.columnHeaderValue.toLowerCase()
        ] || EmptyTestResults;
      const preferenceResults =
        allTestResults['Preference'].dimensions[
          dimension.columnHeaderValue.toLowerCase()
        ] || EmptyTestResults;

      const testResults = {
        passing:
          informationResults.passing +
          structuralResults.passing +
          preferenceResults.passing,
        incomplete:
          informationResults.incomplete +
          structuralResults.incomplete +
          preferenceResults.incomplete,
        failing:
          informationResults.failing +
          structuralResults.failing +
          preferenceResults.failing,
      };

      const calibrationScore = getCalibrationScore({
        testCount: testResults,
      });

      dataPoints.push({
        label: dimension.displayName,
        sliceLink: `/${focusArea}/summary-v2?dimension=${encodeURIComponent(
          dimension.columnHeaderValue,
        )}#top`,
        calibrationScore: calibrationScore,
        passingTests: testResults.passing,
        incompleteTests: testResults.incomplete,
        totalTests: getTotalTestCount(testResults),
      });
    }

    return dataPoints;
  }, [data, focusArea]);

  return (
    <PolarChart
      dataPoints={dataPoints}
      variant={'default'}
      tooltip={DimensionTooltip}
      onDotClick={onDotClick}
      customLayers={[DimensionRadarSliceLayer]}
      calibrationFraming={calibrationFraming}
      dataTestId="dimension-polar-chart"
    />
  );
};
